import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CreditCard, Banknote, CheckCircle2, Circle, AlertTriangle, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { BankAccountsPanel, BankDepositsPanel, BankFeeStep } from '@/components/seller/BankAutoConfirm';
import CardTransactionsPanel from '@/components/seller/CardTransactionsPanel';
import { PaymentServiceGuide } from '@/components/seller/PaymentServiceGuide';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const err = (e, f) => toast.error(e?.response?.data?.detail || f);
const inputCls = 'w-full px-3 py-2 border rounded-xl bg-white dark:bg-[#0A0A0A] border-black/15 dark:border-white/15 text-sm';

export const STATUS_LABEL = {
  not_applied: '미신청', application_pending: '신청 접수', submitted: '외부 가입 진행 중', reviewing: '심사 중',
  additional_info_required: '서류 제출 필요', approved: '승인 완료', connection_required: '연결 필요',
  active: '정상 이용 중', suspended: '이용 중지', rejected: '반려',
};
const TABS = [['overview', '결제 현황'], ['card', '카드결제'], ['card_tx', '카드 거래·취소내역'], ['bank', '무통장입금'], ['deposits', '입금확인·거래내역']];
const FIELDS = [['business_name', '상호'], ['ceo_name', '대표자명'], ['business_number', '사업자번호'], ['phone', '연락처'], ['contact_email', '이메일']];

const Badge = ({ status, testid }) => (
  <span data-testid={testid} className={`px-2 py-0.5 rounded-full text-[11px] font-black ${status === 'active' ? 'bg-emerald-100 text-emerald-700'
    : ['rejected', 'suspended', 'additional_info_required'].includes(status) ? 'bg-red-100 text-red-700'
      : status === 'not_applied' ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-700'}`}>{STATUS_LABEL[status] || status}</span>
);

const Steps = ({ steps, testid }) => (
  <ol className="flex flex-col sm:flex-row gap-1.5 sm:gap-3 text-[11px]" data-testid={testid}>
    {steps.map(([label, done], i) => (
      <li key={label} className={`inline-flex items-center gap-1 font-bold ${done ? 'text-emerald-600' : 'text-gray-400'}`}>
        {done ? <CheckCircle2 size={12} /> : <Circle size={12} />}{i + 1}. {label}
      </li>
    ))}
  </ol>
);

const ApplyForm = ({ svc, prefill, missing, onDone }) => {
  const modes = svc === 'card' ? [['new', '신규 카드결제 신청'], ['existing_mid', '기존 가맹점(MID) 연결']] : [['new', '신규 신청'], ['existing_member', '이미 바로빌 회원']];
  const [mode, setMode] = useState('new');
  const [f, setF] = useState({ ...prefill, existing_mid: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/seller/payments/${svc}/apply`, { ...f, mode }); toast.success('신청이 접수되었습니다.'); onDone(); }
    catch (e) { err(e, '신청 실패'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid={`${svc}-apply-form`}>
      <div className="flex gap-1.5 flex-wrap">
        {modes.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setMode(k)} data-testid={`${svc}-apply-mode-${k}`}
            className={`px-3 py-1 rounded-full text-[11px] font-bold ${mode === k ? 'bg-black text-white' : 'border border-black/10 dark:border-white/10'}`}>{l}</button>
        ))}
      </div>
      <p className="text-[11px] text-gray-500">판매자 정보에서 자동으로 채웠습니다{missing.length ? ' · 비어 있는 항목만 입력해주세요' : ''}.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {FIELDS.map(([k, l]) => (
          <label key={k} className="text-[11px] font-bold space-y-1">{l}
            <input placeholder={l} value={f[k] || ''} onChange={(e) => setF({ ...f, [k]: e.target.value })}
              className={`${inputCls} font-normal ${missing.includes(k) ? 'border-[#FF3B30]' : ''}`} data-testid={`${svc}-apply-${k}`} />
          </label>
        ))}
        {svc === 'card' && mode === 'existing_mid' && (
          <input placeholder="기존 가맹점 MID" value={f.existing_mid} onChange={(e) => setF({ ...f, existing_mid: e.target.value })} className={inputCls} data-testid="card-apply-existing_mid" />
        )}
      </div>
      <button type="button" disabled={busy} onClick={submit} data-testid={`${svc}-apply-submit`}
        className="px-4 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40">신청하기</button>
    </div>
  );
};

const ExternalStep = ({ kind, ready, onDone }) => {
  const [msg, setMsg] = useState(null);
  const path = kind === 'card' ? '/seller/payments/card/start-signup' : '/seller/payments/bank/fee-payment-link';
  const label = kind === 'card' ? '라이트페이 가맹점 신청 페이지로 이동' : '바로빌 이용료 결제하기';
  const go = async () => {
    try {
      const r = await api.post(path);
      if (r.data.ready && r.data.url) { window.open(r.data.url, '_blank', 'noopener,noreferrer'); onDone(); }
      else setMsg(r.data.message);
    } catch (e) { err(e, '이동 실패'); }
  };
  return (
    <div className="space-y-1" data-testid={`${kind}-external-step`}>
      <button type="button" onClick={go} className="px-4 py-2 rounded-full bg-black text-white text-xs font-bold" data-testid={`${kind}-external-go`}>{label}</button>
      {(!ready || msg) && <p className="text-[11px] text-gray-500" data-testid={`${kind}-external-pending`}>{msg || (kind === 'card' ? '가입 경로 연결 준비 중입니다.' : '이용료 결제 경로 연결 준비 중입니다.')}</p>}
      <p className="text-[11px] text-gray-500">새 창에서 진행한 뒤 라모로 돌아오면 진행 상태를 확인할 수 있습니다. 돌아온 것만으로 가입·납부 완료로 처리되지 않습니다.</p>
    </div>
  );
};

const CARD_ONLY_NOTICE = 'LIVEMOA 카드결제는 신용카드 전용으로 운영됩니다. 라이트페이 신청 페이지에 다른 결제수단이 표시될 수 있으나, 실제 이용 가능한 결제수단은 가맹점 계약 및 MID 설정에 따라 결정됩니다.';
const CARD_STAGE_LABEL = { not_applied: '신청 전', received: '신청 접수', reviewing: '계약 심사 중', docs: '추가서류 필요', contract_check: '계약 승인 확인', mid_required: '운영 MID 등록 필요', pg_verify_required: '최초 PG 연결 검증 필요', activation_wait: '활성화 대기', active: '이용 중', suspended: '이용 중지' };
const Check = ({ ok, label, testid }) => <span data-testid={testid} className={ok ? 'text-emerald-700' : 'text-gray-500'}>{label} {ok ? '확인' : '미확인'}</span>;

const CardTab = ({ d, reload, goSettings }) => {
  const c = d.card;
  const st = c.onboarding.status;
  const sg = c.stage || { key: 'not_applied', message: '' };
  const ob = c.onboarding;
  return (
    <div className="space-y-4" data-testid="card-tab">
      <Steps testid="card-steps" steps={[['신청하기', st !== 'not_applied'], ['계약 승인 확인', ob.contract_approved], ['MID·KEY 연결', c.mid_connected && c.key_linked], ['이용 중', sg.key === 'active']]} />
      <div className="rounded-xl bg-black/[0.03] dark:bg-white/5 p-3 text-xs space-y-1" data-testid="card-stage-box">
        <p className="font-black" data-testid="card-stage-label">{CARD_STAGE_LABEL[sg.key]}</p>
        <p data-testid="card-stage-message">{sg.message}</p>
      </div>
      <p className="text-[11px] text-amber-800 bg-amber-50 rounded-xl p-2" data-testid="card-only-notice">{CARD_ONLY_NOTICE}</p>
      <p className="text-xs text-gray-600 dark:text-gray-400">라이트페이 카드결제입니다. 신청 후 공급사 승인과 MID 연결이 확인되어야 이용할 수 있습니다. 가입 접수만으로 카드결제가 켜지지 않습니다.</p>
      {['not_applied', 'rejected'].includes(st) ? <ApplyForm svc="card" prefill={d.prefill} missing={d.prefill_missing} onDone={reload} /> : (
        <div className="text-xs space-y-1" data-testid="card-status-detail">
          <p>진행 상태 <span className="font-bold" data-testid="card-tab-status">{CARD_STAGE_LABEL[sg.key]}</span> {c.onboarding.existing_mid_masked && `· 연결 요청 MID ${c.onboarding.existing_mid_masked}`}</p>
          {c.unmet.length > 0 && <p className="text-amber-700">이용 전 확인 필요: {c.unmet.join(', ')}</p>}
          {c.onboarding.last_message && <p className="text-red-600" data-testid="card-last-message">{c.onboarding.last_message}</p>}
          {c.onboarding.mode === 'new' && ['application_pending', 'submitted', 'additional_info_required'].includes(st) && <ExternalStep kind="card" ready={c.signup_link_ready} onDone={reload} />}
        </div>
      )}
      <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 text-xs space-y-1" data-testid="card-mid-box">
        <p className="font-bold">MID 연결</p>
        <p>MID {c.mid_masked || '미등록'} · KEY {c.key_linked ? '연결됨(암호화 저장)' : '미연결'} · PG 연결 검증 {c.pg_verify_status === 'verified' ? `완료 (${c.pg_verified_env === 'live' ? '운영' : 'TEST'})` : '대기'}</p>
        <p className="flex flex-wrap gap-x-2" data-testid="card-admin-checks"><Check ok={ob.contract_approved} label="계약 승인" testid="card-check-contract" /><Check ok={ob.mid_issued} label="MID 발급" testid="card-check-mid" /><Check ok={ob.card_only_confirmed} label="신용카드 전용" testid="card-check-cardonly" /></p>
        <p className="text-[11px] text-gray-500">계약 승인·MID·신용카드 전용 여부는 라이트페이 확인 후 LIVEMOA 관리자가 반영합니다. 관리자 확인은 PG 연결 검증과 별개입니다.</p>
        <button type="button" onClick={goSettings} className="px-3 py-1 rounded-full border border-black/10 dark:border-white/10 font-bold" data-testid="card-mid-manage">MID 연결 · 관리하기</button>
      </div>
    </div>
  );
};

const BankTab = ({ d, reload }) => {
  const b = d.bank;
  const st = b.onboarding.status;
  return (
    <div className="space-y-4" data-testid="bank-tab">
      <Steps testid="bank-steps" steps={[['신청정보 확인', st !== 'not_applied'], ['바로빌 계정 설정', b.member_status === 'registered'], ['회원사 등록', b.member_status === 'registered' && b.onboarding.partner_verified],
        ['계좌 등록·인증', b.accounts_connected > 0], ['이용료 결제', b.onboarding.fee_payment_confirmed], ['자동입금확인 이용', st === 'active']]} />
      <div className="rounded-xl bg-black/[0.03] dark:bg-white/5 p-3 text-xs space-y-1" data-testid="bank-fee-notice">
        <p className="font-bold">이용료: 계좌당 월 {won(b.fee.per_account_monthly)} (부가세 별도) · 예정 이용료</p>
        <p>이용료는 바로빌이 판매자에게 직접 청구합니다. LIVEMOA 충전금에서 차감하지 않습니다.</p>
        <p>계좌조회 주기: 10분 고정 · 입금·출금 전체 내역을 수집하며, 주문 자동확인은 입금 거래만 대상입니다. 은행·바로빌 사정에 따라 10분보다 늦게 확인될 수 있습니다.</p>
      </div>
      {['not_applied', 'rejected'].includes(st) ? <ApplyForm svc="bank" prefill={d.prefill} missing={d.prefill_missing} onDone={reload} /> : (
        <div className="text-xs space-y-1" data-testid="bank-status-detail">
          <p>신청 상태 <Badge status={st} testid="bank-tab-status" />{b.onboarding.mode === 'existing_member' && ' · 기존 바로빌 회원 연결은 바로빌 확인 후 진행됩니다'}</p>
          {b.unmet.length > 0 && <p className="text-amber-700" data-testid="bank-unmet">이용 전 확인 필요: {b.unmet.join(', ')}</p>}
          {b.onboarding.member_result === 'existing_member_unlinked' && <p className="text-amber-700" data-testid="bank-existing-member">기존 바로빌 회원 연결 확인 필요 · 중복 가입은 진행하지 않습니다.</p>}
          {b.member_status === 'registered' && !b.onboarding.partner_verified && <p className="text-amber-700" data-testid="bank-partner-pending">회원사 등록됨 · 엠지컴퍼니 파트너 귀속 확인 전입니다. 확인 전에는 자동확인을 켤 수 없습니다.</p>}
          {b.member_status === 'registered' && <BankFeeStep done={b.onboarding.fee_payment_confirmed} status={b.onboarding.fee_status} />}
        </div>
      )}
      {st !== 'not_applied' && <BankAccountsPanel onChanged={reload} prefill={{ corp_num: d.prefill.business_number, corp_name: d.prefill.business_name, ceo_name: d.prefill.ceo_name, member_name: d.prefill.ceo_name, tel: d.prefill.phone, email: d.prefill.contact_email, addr1: d.prefill.address, biz_type: d.prefill.biz_type, biz_class: d.prefill.biz_class, hp: d.prefill.mobile }} />}
    </div>
  );
};

const ServiceCard = ({ icon: I, title, status, lines, actions, testid }) => (
  <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 space-y-3" data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <p className="text-sm font-black inline-flex items-center gap-1.5"><I size={15} /> {title}</p>
      <Badge status={status} testid={`${testid}-status`} />
    </div>
    <div className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">{lines.map((l) => <p key={l}>{l}</p>)}</div>
    <div className="flex gap-1.5 flex-wrap">
      {actions.map(([l, fn, id]) => (
        <button key={id} type="button" onClick={fn} data-testid={id} className="px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-[11px] font-bold inline-flex items-center gap-1">{l} <ArrowRight size={11} /></button>
      ))}
    </div>
  </div>
);

const Overview = ({ d, setTab, goOrders }) => {
  const c = d.card; const b = d.bank;
  const stats = [['오늘 결제완료', d.today.total, 'total'], ['카드결제', d.today.card, 'card'], ['무통장 입금확인', d.today.bank_transfer, 'bank'], ['확인 필요 입금', d.today.needs_review, 'review']];
  return (
    <div className="space-y-4">
      <PaymentServiceGuide d={d} setTab={setTab} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {stats.map(([l, v, k]) => (
          <div key={k} className="rounded-2xl bg-black/[0.03] dark:bg-white/5 p-3" data-testid={`pay-stat-${k}`}>
            <p className="text-[11px] text-gray-500 inline-flex items-center gap-1">{k === 'review' && <AlertTriangle size={11} />}{l}</p>
            <p className="text-lg font-black">{k === 'review' ? `${v}건` : won(v)}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ServiceCard icon={CreditCard} title="카드결제 · 라이트페이" status={c.onboarding.status} testid="svc-card"
          lines={[`MID ${c.mid_masked || '미등록'}`, `연결 상태 ${c.mid_connected ? '연결 확인됨' : '연결 확인 필요'}`]}
          actions={c.onboarding.status === 'not_applied' ? [['신청하기', () => setTab('card'), 'svc-card-apply']] : [['관리하기', () => setTab('card'), 'svc-card-manage']]} />
        <ServiceCard icon={Banknote} title="무통장입금 자동확인 · 바로빌" status={b.onboarding.status} testid="svc-bank"
          lines={[`등록 계좌 ${b.accounts_connected}개 연결 / ${b.accounts_total}개`, `자동확인 ${b.auto_confirm_on > 0 ? `켜짐 (${b.auto_confirm_on}개)` : '꺼짐'}`]}
          actions={[[b.onboarding.status === 'not_applied' ? '신청하기' : '계좌관리', () => setTab('bank'), 'svc-bank-accounts'], ['거래내역', () => setTab('deposits'), 'svc-bank-deposits']]} />
      </div>
      <button type="button" onClick={goOrders} className="text-xs font-bold underline" data-testid="pay-go-orders">주문관리로 이동</button>
    </div>
  );
};

export const PaymentManagement = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [d, setD] = useState(null);
  const load = () => api.get('/seller/payments/overview').then((r) => setD(r.data)).catch((e) => err(e, '결제관리 정보를 불러오지 못했습니다.'));
  useEffect(() => { load(); }, []);
  const goOrders = () => navigate('/seller/dashboard?section=orders');
  const goSettings = () => navigate('/seller/dashboard?section=settings&tab=payment');
  return (
    <div className="space-y-4" data-testid="payment-management">
      <div className="flex gap-1 overflow-x-auto pb-1">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`pay-tab-${k}`}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold ${tab === k ? 'bg-black text-white dark:bg-white dark:text-black' : 'border border-black/10 dark:border-white/10'}`}>{l}</button>
        ))}
      </div>
      {!d ? <p className="text-xs text-gray-400">불러오는 중…</p> : (
        <>
          {tab === 'overview' && <Overview d={d} setTab={setTab} goOrders={goOrders} />}
          {tab === 'card' && <CardTab d={d} reload={load} goSettings={goSettings} />}
          {tab === 'card_tx' && <CardTransactionsPanel />}
          {tab === 'bank' && <BankTab d={d} reload={load} />}
          {tab === 'deposits' && (
            <div className="space-y-2">
              <BankDepositsPanel onChanged={load} />
              <button type="button" onClick={goOrders} className="text-xs font-bold underline" data-testid="deposits-go-orders">주문관리로 이동</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentManagement;
